import React, { useState } from 'react';
import { toast } from 'sonner';
import { updateResultRelease } from '../lib/api';

const FLAG_COLOR = { low: '#F59E0B', high: '#EF4444' };

export function ResultReleaseCard({ clinicianId, patientId, result, ranges, onUpdated }) {
  const [released, setReleased] = useState(!!result.released);
  const [hidden, setHidden] = useState(new Set(result.hidden_markers || []));
  const [note, setNote] = useState(result.patient_note || '');
  const [saving, setSaving] = useState(false);

  const markers = Object.entries(result.results || {});
  const dirty =
    released !== !!result.released ||
    note !== (result.patient_note || '') ||
    hidden.size !== (result.hidden_markers || []).length ||
    [...hidden].some((k) => !(result.hidden_markers || []).includes(k));

  const toggleMarker = (k) => setHidden((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateResultRelease(clinicianId, patientId, result.id, { released, hidden_markers: [...hidden], patient_note: note });
      toast.success(released ? `Results released to patient${hidden.size ? ` (${hidden.size} held back)` : ''}` : 'Results held from patient');
      onUpdated?.(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update release settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-dark rounded-2xl p-5 mb-4" data-testid={`release-card-${result.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-lg text-white">{result.test_date}</div>
          <div className="text-xs text-white/50">
            {result.entered_by_name ? `Added by ${result.entered_by_name}` : 'Added by clinical team'}
            {result.released_at ? ` · Released ${new Date(result.released_at).toLocaleDateString('en-GB')} by ${result.released_by_name || ''}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid={`release-status-${result.id}`}
            className={`text-xs font-semibold rounded-full px-3 py-1 ${result.released ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}
          >
            <i className={`fa-solid ${result.released ? 'fa-eye' : 'fa-eye-slash'} mr-1.5`} />
            {result.released ? 'Visible to patient' : 'Held — patient cannot see'}
          </span>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <span className="text-sm text-white/70">Release</span>
            <button
              type="button"
              role="switch"
              aria-checked={released}
              data-testid={`release-toggle-${result.id}`}
              onClick={() => setReleased((v) => !v)}
              className={`relative w-11 h-6 rounded-full transition-colors ${released ? 'bg-emerald-500' : 'bg-white/20'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${released ? 'translate-x-5' : ''}`} />
            </button>
          </label>
        </div>
      </div>

      <div className="text-xs uppercase tracking-wide text-white/40 mb-2">
        Individual values {released ? <span className="normal-case text-white/50">— tap the eye to hold a value back</span> : <span className="normal-case text-white/50">— shown here only; patient sees nothing while held</span>}
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mb-4">
        {markers.map(([key, m]) => {
          const isHidden = hidden.has(key);
          const ref = ranges?.[key];
          const color = m?.flag ? FLAG_COLOR[m.flag] : undefined;
          return (
            <button
              type="button"
              key={key}
              data-testid={`marker-toggle-${result.id}-${key}`}
              onClick={() => toggleMarker(key)}
              className={`flex items-center justify-between rounded-xl border px-3 py-2 text-left transition-base ${isHidden ? 'border-amber-500/40 bg-amber-500/5 opacity-70' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
            >
              <div className="min-w-0">
                <div className="text-sm text-white font-medium truncate">{m?.name || ref?.name || key}</div>
                <div className="text-[11px] text-white/40">{m?.reference_range || (ref ? `${ref.min}-${ref.max}` : '')} {m?.unit || ref?.unit}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                <div className="text-right">
                  <div className="text-white font-semibold" style={{ color }}>{m?.value} <span className="text-xs font-normal text-white/50">{m?.unit}</span></div>
                  {m?.flag ? <div className="text-[11px] font-semibold capitalize" style={{ color }}>{m.flag}</div> : null}
                </div>
                <i className={`fa-solid ${isHidden ? 'fa-eye-slash text-amber-300' : 'fa-eye text-white/40'}`} />
              </div>
            </button>
          );
        })}
      </div>

      <label className="block text-xs text-white/50 mb-1">Note the patient will see with these results <span className="text-white/30">(optional)</span></label>
      <textarea
        data-testid={`release-note-${result.id}`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="e.g. All looks fine — no action needed. We'll recheck in 3 months."
        className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white placeholder-white/30 outline-none focus:border-[var(--aura-purple-light)] resize-none mb-3"
      />

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-white/40">
          {released ? `Patient will see ${markers.length - hidden.size} of ${markers.length} values` : 'Patient will see nothing from this panel'}
        </div>
        <button
          data-testid={`release-save-${result.id}`}
          onClick={save}
          disabled={saving || !dirty}
          className="aura-btn-gradient text-white font-semibold rounded-xl px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-floppy-disk" />} Save visibility
        </button>
      </div>
    </div>
  );
}
